/**
 * The search field (§B search, §B.3, §H.2, §H.3).
 *
 * Glass pill, icon left, clear button right when there is text. Typing is
 * local; the store's query follows 120 ms after the last keystroke (the list
 * filters from the store), and the result count is announced 600 ms after it.
 * Esc clears the text (and stops there, so the card stays open). The store's
 * query can also change from outside (reset, deep link); the text follows it.
 * The search spans every group (selectVisiblePlaces).
 */
import { useEffect, useRef, useState, type ChangeEvent, type JSX, type KeyboardEvent, type RefObject } from "react";
import { Icon, IconButton } from "@/design";
import { actions, getApp, useApp } from "@/store/store";
import { selectVisiblePlaces } from "@/store/selectors";
import { announce } from "@/store/bus";
import { COPY, searchAnnouncement } from "./format";

const FILTER_MS = 120;
const ANNOUNCE_MS = 600;

export interface SearchFieldProps {
  inputRef: RefObject<HTMLInputElement | null>;
  listId: string;
  /** Inside the mobile sheet: focusing the field opens the sheet fully (the keyboard needs the room). */
  embedded?: boolean;
  disabled?: boolean;
}

export function SearchField({ inputRef, listId, embedded, disabled }: SearchFieldProps): JSX.Element {
  const query = useApp((s) => s.query);
  const [text, setText] = useState(query);
  const pushed = useRef(query);
  const filterTimer = useRef(0);
  const announceTimer = useRef(0);

  // Outside changes to the query (reset, deep link, a chip press clearing it) win over the field.
  useEffect(() => {
    if (query === pushed.current) return;
    pushed.current = query;
    window.clearTimeout(filterTimer.current);
    setText(query);
  }, [query]);

  useEffect(
    () => () => {
      window.clearTimeout(filterTimer.current);
      window.clearTimeout(announceTimer.current);
    },
    [],
  );

  const push = (v: string) => {
    pushed.current = v;
    actions.setQuery(v);
  };

  const scheduleAnnounce = (v: string) => {
    window.clearTimeout(announceTimer.current);
    if (!v.trim()) return;
    announceTimer.current = window.setTimeout(() => {
      const s = getApp();
      if (s.phase === "attract" || !s.query.trim()) return;
      announce(searchAnnouncement(selectVisiblePlaces(s).length));
    }, ANNOUNCE_MS);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setText(v);
    window.clearTimeout(filterTimer.current);
    filterTimer.current = window.setTimeout(() => push(v), FILTER_MS);
    scheduleAnnounce(v);
  };

  const clear = (refocus: boolean) => {
    window.clearTimeout(filterTimer.current);
    window.clearTimeout(announceTimer.current);
    setText("");
    push("");
    if (refocus) inputRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape" && text) {
      e.preventDefault();
      e.stopPropagation();
      clear(false);
    } else if (e.key === "Enter") {
      // Commit at once rather than waiting for the debounce.
      window.clearTimeout(filterTimer.current);
      if (text !== pushed.current) push(text);
    }
  };

  const onFocus = () => {
    if (embedded && getApp().sheet !== "full") actions.setSheet("full");
  };

  return (
    <div className="ld-loc-panel-search" role="search">
      <Icon name="search" size={18} />
      <input
        ref={inputRef}
        type="search"
        value={text}
        placeholder={COPY.searchPlaceholder}
        aria-label={COPY.searchAria}
        aria-controls={listId}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="search"
        disabled={disabled}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
      />
      {text && <IconButton className="ld-loc-panel-clear" icon="close" label={COPY.searchClear} size={32} iconSize={16} tip={null} onClick={() => clear(true)} />}
    </div>
  );
}
